import React from 'react';
import styled from 'styled-components';
import { colors } from 'bench-style-constants';
import { Button } from 'bench-internal-theme';
import { FeatureValue, useFeatureFlags } from '@bench/ff-client';
import { SplitFeatureFlags } from 'bk-web-app/features';
import { useGeneralLedger } from './contexts';
import type { TaskStatusFilter } from './types';

const SidebarWrapper = styled.div`
  background: ${colors.white};
  border-left: 1px solid ${colors.silt};
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const SidebarHeader = styled.div`
  align-items: center;
  border-bottom: 1px solid ${colors.silt};
  display: flex;
  justify-content: space-between;
  padding: 16px 24px;
`;

const SidebarTitle = styled.div`
  color: ${colors.charcoal};
  font-size: 18px;
  font-weight: 600;
  line-height: 24px;
`;

const SidebarContent = styled.div`
  flex: 1;
  padding: 24px;
`;

const SettingGroup = styled.div`
  margin-bottom: 24px;
`;

const SettingGroupTitle = styled.div`
  color: ${colors.smoke};
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  margin-bottom: 12px;
  line-height: 16px;
`;

const SettingItem = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
  width: 100%;
`;

const SettingLabel = styled.div`
  color: ${colors.charcoal};
  font-size: 14px;
  line-height: 20px;
`;

const StyledSelect = styled.select`
  background: ${colors.white};
  border: 1px solid ${colors.silt};
  border-radius: 4px;
  color: ${colors.charcoal};
  font-size: 14px;
  padding: 4px 8px;
  cursor: pointer;

  &:hover {
    border-color: ${colors.smoke};
  }

  &:focus {
    outline: none;
    border-color: ${colors.copper};
  }
`;

type SettingsSidebarProps = {
  dismiss: () => void;
  hasQualityIssues?: boolean;
};

const SettingsSidebar = ({ dismiss, hasQualityIssues = false }: SettingsSidebarProps) => {
  const {
    filters: { showAccounts, showQualityIssues, taskStatusFilter },
    showSourceColumn,
    updateFilters,
    toggleSourceColumn,
  } = useGeneralLedger();

  // Check if the confirm categorization tasks feature is enabled
  const [
    {
      featureContext: { features },
    },
  ] = useFeatureFlags();
  const isConfirmCategorizationTasksEnabled =
    features[SplitFeatureFlags.CONFIRM_CATEGORIZATION_TASKS]?.value === FeatureValue.FEATURE_ON;
  return (
    <SidebarWrapper>
      <SidebarHeader>
        <SidebarTitle>General Ledger Settings</SidebarTitle>
        <Button icon="close" onClick={dismiss} styleType="total-ghost" size="small" />
      </SidebarHeader>
      <SidebarContent>
        <SettingGroup>
          <SettingGroupTitle>Display Options</SettingGroupTitle>
          <SettingItem>
            <SettingLabel>Show Accounts</SettingLabel>
            <Button
              icon={showAccounts ? 'squareChecked' : 'squareOutlined'}
              onClick={() => updateFilters({ showAccounts: !showAccounts })}
              styleType="total-ghost"
              size="small"
            />
          </SettingItem>
          <SettingItem>
            <SettingLabel>Show Quality Issues Summary</SettingLabel>
            <Button
              icon={hasQualityIssues && showQualityIssues ? 'squareChecked' : 'squareOutlined'}
              onClick={() => updateFilters({ showQualityIssues: !showQualityIssues })}
              styleType="total-ghost"
              size="small"
              disabled={!hasQualityIssues}
            />
          </SettingItem>
          {isConfirmCategorizationTasksEnabled && (
            <SettingItem>
              <SettingLabel>Task Filter</SettingLabel>
              <StyledSelect
                value={taskStatusFilter || 'none'}
                onChange={e =>
                  updateFilters({ taskStatusFilter: e.target.value as TaskStatusFilter })
                }
                aria-label="Task status filter"
              >
                <option value="none">Show All Entries</option>
                <option value="all">Entries with Any Task</option>
                <option value="pending">Entries with Pending Tasks</option>
                <option value="confirmed">Entries with Confirmed Tasks</option>
              </StyledSelect>
            </SettingItem>
          )}
        </SettingGroup>
        <SettingGroup>
          <SettingGroupTitle>Columns</SettingGroupTitle>
          <SettingItem>
            <SettingLabel>Show Source Column</SettingLabel>
            <Button
              icon={showSourceColumn ? 'squareChecked' : 'squareOutlined'}
              onClick={() => toggleSourceColumn()}
              styleType="total-ghost"
              size="small"
            />
          </SettingItem>
        </SettingGroup>
      </SidebarContent>
    </SidebarWrapper>
  );
};

export default SettingsSidebar;
